import { ReactNode, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, Home, LogIn, LogOut, Menu, X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiService, isWriteEnabled, queryKeys } from '@/services/api';
import { useAuth } from '@/auth/AuthProvider';

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { isAuthenticated, isAuthEnabled, userName, login, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: health } = useQuery({
    queryKey: queryKeys.health,
    queryFn: apiService.checkHealth,
    refetchInterval: 60000,
    retry: 1,
  });

  const isHealthy = health?.status === 'healthy';

  const navLinkClass = (active: boolean) =>
    `flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active ? 'bg-white/15 text-white' : 'text-indigo-200 hover:bg-white/10 hover:text-white active:bg-white/20'
    }`;

  return (
    <div className="h-screen flex flex-col bg-slate-50 overflow-hidden">
      {/* Header */}
      <header className="header-gradient text-white shadow-lg flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center gap-2.5 text-white hover:text-brand-200 transition-colors"
            >
              <Activity className="w-5 h-5 text-brand-400" />
              <span className="text-lg font-bold tracking-tight">NAPA Rankings</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1">
              <Link to="/" className={navLinkClass(location.pathname === '/')}>
                <Home className="w-3.5 h-3.5" />
                Dashboard
              </Link>

              {isWriteEnabled && isAuthEnabled && (
                <div className="flex items-center gap-1 ml-1">
                  {isAuthenticated ? (
                    <button onClick={logout} className={navLinkClass(false)} title={`Signed in as ${userName}`}>
                      <LogOut className="w-3.5 h-3.5" />
                      {userName?.split(' ')[0]} · Sign out
                    </button>
                  ) : (
                    <button onClick={login} className={navLinkClass(false)}>
                      <LogIn className="w-3.5 h-3.5" />
                      Sign in
                    </button>
                  )}
                </div>
              )}

              {/* Status */}
              <div className="flex items-center gap-2 ml-3 pl-3 border-l border-white/20">
                <div className={`w-2 h-2 rounded-full ${isHealthy ? 'bg-emerald-400 pulse-dot' : 'bg-red-400'}`} />
                <span className="text-xs text-indigo-200">
                  {isHealthy ? 'Online' : 'Offline'}
                </span>
                {health?.version && (
                  <span className="text-xs text-indigo-300/60">v{health.version}</span>
                )}
              </div>
            </nav>

            {/* Mobile: status dot + hamburger */}
            <div className="flex md:hidden items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${isHealthy ? 'bg-emerald-400 pulse-dot' : 'bg-red-400'}`} />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 -mr-2 rounded-md text-indigo-200 hover:text-white hover:bg-white/10 active:bg-white/20 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Mobile dropdown menu */}
          {mobileMenuOpen && (
            <nav className="md:hidden pb-3 border-t border-white/10 mt-1 pt-2 space-y-1">
              <Link
                to="/"
                className={navLinkClass(location.pathname === '/')}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Link>

              {isWriteEnabled && isAuthEnabled && (
                isAuthenticated ? (
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className={navLinkClass(false) + ' w-full'}>
                    <LogOut className="w-4 h-4" />
                    {userName?.split(' ')[0]} · Sign out
                  </button>
                ) : (
                  <button onClick={() => { login(); setMobileMenuOpen(false); }} className={navLinkClass(false) + ' w-full'}>
                    <LogIn className="w-4 h-4" />
                    Sign in
                  </button>
                )
              )}

              <div className="flex items-center gap-2 px-3 pt-2 text-xs text-indigo-300">
                <div className={`w-2 h-2 rounded-full ${isHealthy ? 'bg-emerald-400' : 'bg-red-400'}`} />
                {isHealthy ? 'Online' : 'Offline'}
                {health?.version && <span className="text-indigo-300/60">v{health.version}</span>}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 min-h-0 overflow-y-auto flex flex-col">{children}</main>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between text-xs text-slate-400">
            <span>© 2025 NAPA Rankings</span>
            <a
              href="https://poolshooters.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-brand-600 transition-colors"
            >
              poolshooters.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
